import React, { useCallback, useMemo } from 'react'
import * as S from '../styles'
import { formatNumberBR } from 'src/utils'
import { SalesReceivementBatch } from 'src/features/receivement/ReceivementTypes'
import { dropdownOptions } from './ActionDropdown'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { actions } from 'src/features/receivement/ReceivementState'

interface Props {
  batch: SalesReceivementBatch;
  salesPerDay: number;
  quantityErrorMessage?: string;
  handleErrorQuantity: (value: number, option?: string) => void;
}

export const AcceptQuantityInput = React.memo(
  ({
    batch,
    salesPerDay,
    quantityErrorMessage,
    handleErrorQuantity,
  }: Props) => {
    const dispatch = useDispatch()
    const batchState = useSelector(
      (state) => state.receivement.batchesStateByBatchItemOrderIds[batch.idsBatchItemOrder.join(',')],
    )

    const handleQuantity = React.useCallback((event) => {
      let value = parseInt(event.target.value.replace(/[^0-9]/g, ''), 10)
      if (batch.measureUnit.isKg) {
        value /= 1000
      }

      handleErrorQuantity(
        value,
        batchState.option ? dropdownOptions[batchState.option] : undefined,
      )

      if (Number.isNaN(value)) {
        dispatch(
          actions.updateBatchState({
            [batch.idsBatchItemOrder.join(',')]: {
              ...batchState,
              acceptedQuantity: 0,
              option: batchState.option,
            },
          }),
        )
        return
      }

      const acceptDamage = value > batch.usefulQuantity

      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            acceptedQuantity: value,
            returnQuantity: batch.totalQuantity - value,
            stockDays: salesPerDay !== 0 ? value / salesPerDay : 0,
            acceptDamage,
            option: batchState.option,
          },
        }),
      )
    }, [batch, batchState, salesPerDay, dispatch, handleErrorQuantity])

    const formatInputNumber = useCallback(
      (acceptedQuantity) => {
        if (typeof acceptedQuantity !== 'number') {
          return ''
        }

        if (batch.measureUnit.isKg) {
          return formatNumberBR(acceptedQuantity, 3)
        }

        return formatNumberBR(acceptedQuantity)
      },
      [batch.measureUnit.isKg],
    )

    const acceptedQuantityFormatted = useMemo(
      () => formatInputNumber(batchState.acceptedQuantity),
      [batchState.acceptedQuantity, formatInputNumber],
    )

    return (
      <S.QuantityWrapper>
        <S.Input
          aria-label='Quantidade a ser aceita do lote'
          data-testid='qty-input'
          type='text'
          error={!!quantityErrorMessage}
          value={acceptedQuantityFormatted}
          onChange={handleQuantity}
          readOnly={
          batchState.option
            ? dropdownOptions[batchState.option] !== dropdownOptions.PARTIAL
            : true
        }
        />
        {quantityErrorMessage && <S.Error>{quantityErrorMessage}</S.Error>}
      </S.QuantityWrapper>
    )
  },
)
